import { Card, SwitchOnChangeData } from "@fluentui/react-components";
import { ChangeEvent, memo, useCallback, useRef, useState } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { EmptyPlaceholder } from "../../../components/EmptyPlaceholder";
import { LoadingContainer } from "../../../components/Loading";
import { Redline } from "../../../components/Redline";
import { IClause, IDocument } from "../../../interfaces";
import { UnusualTextPhrases } from "../../../models/responses/suggestion";
import { useSuggestionToolbarStyles } from "../../SuggestionResult/styles/SuggestionToolbar.styles";
import { useLocalStyles } from "../styles/SimilarClauseRenderer.styles";
import { RenderSuggestion } from "./RenderSuggestion";
type SimilarClauseRendererProps = {
  isLoading?: boolean;
  similarClauses: Array<IClause>;
  originalText: string;
  shouldRedline?: boolean;
  unusualPhrases?: Record<string, UnusualTextPhrases>;
  onSimilarClauseRedline?: (enabled: boolean) => void;
  onOpenDocumentViewer: (document: IDocument, clause?: IClause) => void;
  workspaceId: string;
  appId: string;
};

export const SimilarClauseRenderer = memo(
  ({
    similarClauses,
    shouldRedline,
    originalText,
    isLoading,
    onSimilarClauseRedline,
    onOpenDocumentViewer,
    workspaceId,
    appId,
  }: SimilarClauseRendererProps) => {
    const [shouldRedlineSimilarClause, setShouldRedlineSimilarClause] = useState<boolean>(() => !!shouldRedline);

    const virtuosoRef = useRef<VirtuosoHandle>(null);

    const onToggleSimilarRedline = (ev: ChangeEvent, data: SwitchOnChangeData) => {
      setShouldRedlineSimilarClause(data.checked as boolean);
      onSimilarClauseRedline?.(data.checked as boolean);
    };
    const styles = useSuggestionToolbarStyles();
    const localStyles = useLocalStyles();

    const itemContent = useCallback(
      (index: number, clause: IClause) => (
        <RenderSuggestion
          idx={index}
          clause={clause}
          workspaceId={workspaceId}
          appId={appId}
          context={{
            originalText,
            shouldRedlineSimilarClause,
            onOpenDocumentViewer,
          }}
        />
      ),
      [originalText, shouldRedlineSimilarClause, onOpenDocumentViewer, workspaceId, appId]
    );

    return (
      <LoadingContainer
        isLoading={Boolean(isLoading)}
        size="large"
        label="Loading similar clauses"
        labelPosition="below"
      >
        {similarClauses.length > 0 && (
          <Card
            appearance="subtle"
            className={localStyles.similarClauseRedlineCard}
            key={"similarClausesRedlineSwitch"}
          >
            <Redline redlined={shouldRedlineSimilarClause} ontoggleRedlining={onToggleSimilarRedline} styles={styles} />
          </Card>
        )}
        <Virtuoso
          ref={virtuosoRef}
          data={similarClauses}
          className={localStyles.virtuoso}
          followOutput={(isBottom: boolean) => (isBottom ? "smooth" : false)}
          itemContent={itemContent}
          components={{
            EmptyPlaceholder: () => <EmptyPlaceholder text="No Similar Clause found" />,
          }}
        />
      </LoadingContainer>
    );
  }
);
